import { Dispatch, SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Input } from '../ui/input';
import StyledButton from '../StyledHtmlElements/Button';
import { useToast } from '@/hooks/use-toast';
import { resetPassword } from '@aws-amplify/auth';
import { validateEmail } from './helpers';

type Props = {
  setForgotPasswortEmail: Dispatch<SetStateAction<string>>;
};

const formSchema = z.object({
  email: z
    .string()
    .min(2, {
      message: 'Die Email muss mindestens 2 Zeichen lang sein.',
    })
    .max(50, {
      message: 'Die Email darf maximal 50 Zeichen lang sein.',
    })
    .refine(validateEmail, {
      message: 'Keine gültige Email-Adresse',
    }),
});

const ResetPasswordForm = ({ setForgotPasswortEmail }: Props) => {
  const { toast } = useToast();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  async function onSubmit({ email }: z.infer<typeof formSchema>) {
    try {
      await resetPassword({ username: email });
      setForgotPasswortEmail(email);

      toast({
        title: 'Bestätigungscode gesendet',
        description: `Der Bestätigungscode wurde an ${email} gesendet.`,
      });
    } catch (error) {
      console.error('error', error);
      setForgotPasswortEmail('');
      let message = 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';

      if (error && typeof error === 'object' && 'message' in error) {
        message = error.message as string;
      }

      toast({
        variant: 'destructive',
        title: 'Fehler beim Bestätigungscode erneut senden',
        description: message,
      });
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className=" font-heading text-center tracking-widest text-xl">Passwort zurücksetzen</h1>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="sign-in-email">Email</FormLabel>
                <FormControl>
                  <Input id="sign-in-email" placeholder="Geben Sie Ihre E-Mail-Adresse ein" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-col gap-4 pt-4">
            <StyledButton type="submit" className="w-full">
              Bestätigungscode senden
            </StyledButton>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default ResetPasswordForm;
